import { createFileRoute } from "@tanstack/react-router";
import type { Doc } from "building-types/doc";
import type { DocEntityBase } from "building-types/entities/docEntityBase";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { LabelableDocEntityTypes } from "building-types/handles/labelableEntityHandle";
import { uploadFile } from "common/utils/uploadFile";
import getPageTitle from "lib/utils/getPageTitle";
import { useState } from "react";
import type { ReactNode } from "react";
import { Helmet as Head } from "react-helmet-async";
import withAuthentication from "../../components/withAuthentication";

// This page is used to repair home files that have been corrupted by the Higharc editor.
// Sometimes urgent fixes are needed to get the file working again, so we aren't able to
// wait for the next release with a migration fix. Also this tool has the benefit of being
// type checked with the latest version of the doc.

const RepairHomePage = (): ReactNode => {
  const [originalDoc, setOriginalDoc] = useState<Doc | null>(null);
  const [repairedDoc, setRepairedDoc] = useState<Doc | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [repairLog, setRepairLog] = useState<string[]>([]);

  const handleFileUpload = async (): Promise<void> => {
    try {
      setIsProcessing(true);
      setError(null);
      setRepairLog([]);

      const { FILE_EXT, docParse } = await import("doc/docMarshal");
      const { body } = await uploadFile("application/json," + FILE_EXT);

      // Parse the uploaded home file
      const doc = docParse(body);
      setOriginalDoc(doc);

      // Perform repairs
      const repaired = await performRepairs(doc);
      setRepairedDoc(repaired);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to process file");
    } finally {
      setIsProcessing(false);
    }
  };

  const performRepairs = async (doc: Doc): Promise<Doc> => {
    const [{ docMergeEntityProps }, { docEntityIsLabelable }, { default: docForEachEntity }] = await Promise.all([
      import("doc/docMerge"),
      import("doc/entity/docEntityHandleTools"),
      import("doc/entity/docForEachEntity"),
    ]);
    const log: string[] = [];
    let repairedDoc = { ...doc };

    log.push("Starting repair process...");
    log.push(`Processing document: ${doc.name}`);

    let numRepaired = 0;

    docForEachEntity(
      doc,
      (entity) => {
        const docEntity = entity as DocEntityBase;
        if (docEntityIsLabelable(docEntity)) {
          const handle = { type: docEntity.entityType, id: docEntity.id } as DocEntityHandle;
          if (typeof docEntity.labels === "boolean") {
            repairedDoc = docMergeEntityProps(repairedDoc, handle, { labels: {} });
            numRepaired++;
            log.push(`Fixed corrupted labels on ${docEntity.entityType} "${docEntity.name || docEntity.id}"`);
          }

          // Check if entity has viewFilter property and if viewFilter.labels is corrupted (boolean instead of object)
          if (
            "viewFilter" in docEntity &&
            docEntity.viewFilter &&
            typeof docEntity.viewFilter === "object" &&
            typeof (docEntity.viewFilter as any).labels === "boolean"
          ) {
            // Only process entity types that can have viewFilter
            if (
              docEntity.entityType === "PLAN_VIEW" ||
              docEntity.entityType === "VIEW_3D" ||
              docEntity.entityType === "ELEVATION_VIEW" ||
              docEntity.entityType === "SECTION_VIEW"
            ) {
              repairedDoc = docMergeEntityProps(repairedDoc, handle, {
                viewFilter: { ...docEntity.viewFilter, labels: {} },
              });
              numRepaired++;
              log.push(
                `Fixed corrupted viewFilter.labels on ${docEntity.entityType} "${docEntity.name || docEntity.id}"`,
              );
            }
          }
        }
      },
      true,
      LabelableDocEntityTypes,
    );

    log.push(`Repaired ${numRepaired} entities`);

    setRepairLog(log);
    return repairedDoc;
  };

  const downloadRepairedFile = async (): Promise<void> => {
    if (!repairedDoc) {
      return;
    }

    const { docSerialize } = await import("doc/docMarshal");
    const serializedDoc = docSerialize(repairedDoc);
    const blob = new Blob([serializedDoc], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `repaired-${repairedDoc.name || "home"}.home`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Head>
        <title>{getPageTitle("Home Repair Tool")}</title>
      </Head>

      <div style={{ padding: "20px", maxWidth: "800px", margin: "0 auto" }}>
        <h1>Home File Repair Tool</h1>
        <p>Upload a Higharc .home file to repair integrity errors and download the repaired version.</p>

        <div style={{ marginBottom: "20px" }}>
          <button
            onClick={handleFileUpload}
            disabled={isProcessing}
            style={{
              padding: "10px 20px",
              fontSize: "16px",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: isProcessing ? "not-allowed" : "pointer",
            }}
          >
            {isProcessing ? "Processing..." : "Upload Home File"}
          </button>
        </div>

        {error && (
          <div
            style={{
              padding: "10px",
              backgroundColor: "#f8d7da",
              color: "#721c24",
              border: "1px solid #f5c6cb",
              borderRadius: "4px",
              marginBottom: "20px",
            }}
          >
            Error: {error}
          </div>
        )}

        {originalDoc && (
          <div style={{ marginBottom: "20px" }}>
            <h3>Original Document</h3>
            <p>
              <strong>Name:</strong> {originalDoc.name}
            </p>
            <p>
              <strong>Schema Version:</strong> {originalDoc.schemaVersion}
            </p>
            <p>
              <strong>Is Parsed:</strong> {originalDoc.isParsed ? "Yes" : "No"}
            </p>
          </div>
        )}

        {repairLog.length > 0 && (
          <div style={{ marginBottom: "20px" }}>
            <h3>Repair Log</h3>
            <div
              style={{
                backgroundColor: "#f8f9fa",
                padding: "10px",
                borderRadius: "4px",
                maxHeight: "200px",
                overflowY: "auto",
              }}
            >
              {repairLog.map((log, index) => (
                <div key={index} style={{ marginBottom: "5px" }}>
                  {log}
                </div>
              ))}
            </div>
          </div>
        )}

        {repairedDoc && (
          <div style={{ marginBottom: "20px" }}>
            <h3>Repaired Document</h3>
            <p>
              <strong>Name:</strong> {repairedDoc.name}
            </p>
            <p>
              <strong>Schema Version:</strong> {repairedDoc.schemaVersion}
            </p>
            <p>
              <strong>Is Parsed:</strong> {repairedDoc.isParsed ? "Yes" : "No"}
            </p>

            <button
              onClick={downloadRepairedFile}
              style={{
                padding: "10px 20px",
                fontSize: "16px",
                backgroundColor: "#28a745",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Download Repaired File
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export const Route = createFileRoute("/debug/repair-home")({
  component: withAuthentication(RepairHomePage, { superAdmin: true }),
});
